import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';

import Alert, { SeverityType } from 'components/Common/Alert/Alert';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import { ScrollToErrorFormik } from 'components/Common/ScrollToErrorFormik/ScrollToErrorFormik';
import RowBuilder from 'components/Common/FormBuilder/RowBuilder';
import TurnstileFormikRow, {
  TurnstileRef,
} from 'components/Common/Cloudflare/TurnstileFormikRow';
import Button from 'components/UI/Buttons/Button/Button';
import Loading from 'components/Common/Loading/Loading';
import { FLASH_MESSAGE_STATE_KEY } from 'contexts/flashMessageContext';
import { useConvertMD5 } from 'hooks/useConvertMD5';
import { useCustomer } from 'hooks/useCustomer';
import useCurrentCountry from 'hooks/useCurrentCountry';
import { initialValuesSignUp } from 'settings/initialValuesForm';
import { fetcherWithBody } from 'services/fetcher';
import { apiUrls } from 'services/api-urls';
import { replacePhoneNumberWithoutSpace } from 'services/formatter';
import { getRoutesUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import validationSchema from 'schemas/SignUpErrors';
import { signUpFields } from 'settings/fields/signUp';

import styles from './SignupForm.module.scss';

type ErrorType = {
  message: string | React.ReactElement;
  severity: SeverityType;
  afterCallback?: () => void;
};

type Props = {
  isCheckoutPage?: boolean;
  classes?: Partial<{
    container: string;
  }>;
};

export default function SignUpForm({
  isCheckoutPage = false,
  classes,
}: Props): JSX.Element {
  const { t } = useTranslation(['signUpPage']);
  const navigate = useNavigate();
  const { refreshCustomer } = useCustomer();
  const turnstile = useRef<TurnstileRef>();
  const isTurnstileDesktop = useMediaQuery({ minWidth: '359px' });
  const [errors, setErrors] = useState<ErrorType[] | undefined>();
  const errorRef = useRef<HTMLDivElement>(null);
  const { isUk } = useCurrentCountry();
  const [isLoading, setLoading] = useState<boolean>(false);
  const fields = useMemo(
    () =>
      signUpFields.sort((fieldA, fieldB) => {
        if (fieldA.order < fieldB.order) {
          return -1;
        } else if (fieldA.order > fieldB.order) {
          return 1;
        }

        return 0;
      }),
    [signUpFields]
  );

  useEffect(() => {
    const pageFooterEl = document.querySelector('.page-footer');

    if (isCheckoutPage) {
      pageFooterEl?.classList.add(styles.footerCheckout);
    }

    return () => {
      if (isCheckoutPage) {
        pageFooterEl?.classList.remove(styles.footerCheckout);
      }
    };
  }, [isCheckoutPage]);

  const submitForm = async (
    values: typeof initialValuesSignUp,
    actions: FormikHelpers<typeof initialValuesSignUp>
  ) => {
    setLoading(true);

    errors && setErrors(undefined);

    const {
      civility,
      email,
      dateOfBirth,
      password,
      surname,
      firstName,
      newsAfibelMail,
      newsAfibelSms,
      newsPartnerAfibelMail,
      landLine,
      mobileNumber,
      nameWay,
      nameComplement,
      nameAddress,
      locality,
      postCode,
      city,
      country,
      turnstileToken,
    } = values;

    const body = {
      civility: civility,
      email: email.toLowerCase(),
      ...(dateOfBirth !== '' && { dateOfBirth: dateOfBirth }),
      passwordMail: useConvertMD5(password),
      lastName: surname,
      firstName: firstName,
      mailOption: newsAfibelMail,
      smsOption: newsAfibelSms || null,
      partOption: newsPartnerAfibelMail,
      phone: replacePhoneNumberWithoutSpace(landLine),
      cellPhone: replacePhoneNumberWithoutSpace(mobileNumber),
      addresses: {
        P: {
          type: 'P',
          street: nameWay,
          nameComplement: nameComplement,
          nameAddress: nameAddress,
          saidPlace: locality,
          postalCode: postCode,
          city: city,
          country: country,
        },
      },
    };

    const createResponse = (await fetcherWithBody(
      apiUrls.getCustomerCreate(),
      { ...body, turnstileToken },
      true
    )) as Response;

    if (createResponse.status === 200) {
      // Handle successful creation
      pushGtmEvent({
        event: '_account_creation',
        user_login_type: isCheckoutPage ? 'checkout' : 'compte',
      });

      // Signup creates the session
      refreshCustomer().then(() => {
        localStorage.setItem(
          'user_login_type',
          isCheckoutPage ? 'checkout' : 'compte'
        );

        if (isCheckoutPage) {
          navigate(getRoutesUri<string>('checkoutShipping'));

          return;
        }

        navigate(getRoutesUri('customerAccountInformation') as string, {
          state: {
            [FLASH_MESSAGE_STATE_KEY]: {
              message: t('LABEL_CREATE_ACCOUNT_SUCCESS'),
              severity: 'success',
            },
          },
        });
      });

      return;
    }

    if (createResponse.status === 409) {
      setErrors([
        { message: t('LABEL_ERROR_EMAIL_ALREADY_TAKEN'), severity: 'warning' },
      ]);

      turnstile.current?.reset();
      actions.resetForm();
      actions.setFieldValue('email', '', false);

      // Without timeout, the scroll is blocking by resetForm
      setTimeout(() => errorRef.current?.scrollIntoView(), 100);

      setLoading(false);

      return;
    }

    try {
      const json = await createResponse.json();

      if (json.error) {
        setErrors([{ message: json.error, severity: 'error' }]);
      } else if (json.errors && Object.keys(json.errors).length > 0) {
        setErrors(
          Object.values(json.errors).map(message => ({
            message,
            severity: 'error',
          })) as ErrorType[]
        );
      }
    } catch (e) {
      console.error(e);
      setErrors([
        {
          message: t('LABEL_ERROR_SIGNUP'),
          severity: 'error',
        },
      ]);
    } finally {
      turnstile.current?.reset();
      errorRef.current?.scrollIntoView();
      setLoading(false);
    }
  };

  return (
    <>
      {errors?.length && (
        <div ref={errorRef}>
          {errors.map((error, index) => (
            <Alert
              key={index}
              severity={error.severity}
              label={error.message as string}
            />
          ))}
        </div>
      )}
      <Formik
        initialValues={{
          ...initialValuesSignUp,
          country: isUk ? t('LABEL_UNITED_KINGDOM') : t('LABEL_FRANCE'),
        }}
        validationSchema={validationSchema(isUk, t)}
        enableReinitialize={true}
        validateOnMount
        onSubmit={submitForm}
      >
        <Form
          noValidate
          data-testid={'create-form'}
          className={classNames(styles.formContainer, classes?.container)}
        >
          <FormikErrorsGtmLogger
            page_type="identification"
            form_type="création du compte"
          />

          {fields.map(field => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Extracted for non-reuse.
            const { order, ...fieldData } = field;

            return (
              <RowBuilder key={field.name} field={fieldData} styles={styles} />
            );
          })}

          <TurnstileFormikRow
            turnstileRef={turnstile}
            turnstileProps={{
              className: styles.turnstileWidget,
              options: {
                action: 'create',
                size: isTurnstileDesktop ? 'flexible' : 'compact',
              },
            }}
          />

          <Button
            type={'submit'}
            model={'primary'}
            className={classNames(
              styles.submitButton,
              isCheckoutPage && styles.checkoutSubmitButton
            )}
          >
            {t('CREATE_SUBMIT_BUTTON')}
          </Button>
          <ScrollToErrorFormik />
        </Form>
      </Formik>
      {isLoading && <Loading loading />}
    </>
  );
}
